import { InMemoryCache, defaultDataIdFromObject, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { ApolloClient } from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import fragment from '@/generated-types/fragment';
import DebounceLink from 'apollo-link-debounce';
import { GraphqlAuthGuard } from '@/Authentication/utils/guard';
import { getToken } from '@/Authentication/utils/token';
import { GQLErrorHandler } from '@/util/errors';
const DEBOUNCE_TIME = 100;
const isProd = process.env.NODE_ENV === 'production';
const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: fragment
});
const authMiddleware = new ApolloLink((operation, forward) => {
    if (process.browser) {
        const currentLocale = globalThis.$i18n.locale;
        // add the authorization to the headers
        operation.setContext({
            headers: {
                authorization: `Bearer ${getToken()}` || null,
                'X-Locale': currentLocale ? currentLocale.toUpperCase() : undefined
            }
        });
    }
    return forward(operation);
});
let cache = null;
let legacyClient = null;
let apolloClient = null;
// Create the apollo client
export function createApolloClients() {
    cache = cache
        ? cache
        : new InMemoryCache({
            fragmentMatcher,
            dataIdFromObject: object => {
                switch (object.__typename) {
                    case 'Booking':
                        return object?.booking_id?.toString();
                    case 'GetAccountProfile':
                        return object?.accountId?.toString();
                    case 'EventNotification':
                        return object?.event_notification_id?.toString();
                    case 'InventoryService':
                        return object?.system_service_id?.toString();
                    case 'OrganisationBank':
                        return object?.organisation_bank_id?.toString();
                    case 'SpaceAvailability':
                        return object?.space_availability_id?.toString();
                    case 'TeamMember':
                        return object?.email;
                    default:
                        return defaultDataIdFromObject(object);
                }
            }
        });
    legacyClient = legacyClient
        ? legacyClient
        : new ApolloClient({
            /** TODO: find better way to handle Circular dependencies */
            link: ApolloLink.from([
                new DebounceLink(DEBOUNCE_TIME),
                onError(GQLErrorHandler),
                onError(GraphqlAuthGuard),
                createHttpLink({
                    uri: process.env.VUE_APP_GRAPHQL_URL_PROXY
                })
            ]),
            cache
        });
    apolloClient = apolloClient
        ? apolloClient
        : new ApolloClient({
            /** TODO: find better way to handle Circular dependencies */
            link: ApolloLink.from([
                authMiddleware,
                onError(GQLErrorHandler),
                onError(GraphqlAuthGuard),
                // Was a `split()` sending subscription operations to a
                // WebSocketLink and everything else here. There are no
                // subscriptions left -- the two that existed were ten-second
                // polling loops wearing a websocket and are ordinary polled
                // queries now -- so the split always took this branch and is gone,
                // along with `apollo-link-ws` and `/graphql_ws/`.
                createHttpLink({
                    uri: process.env.VUE_APP_GRAPHQL_URL
                })
            ]),
            cache
        });
    return { apolloClient, legacyClient };
}
